<template>
  <div :style="customStyle" class="aretv sedna-sports games">
    <div class="slider">
      <div class="widgetLogo">
        <img :src="`/img/are_logo.svg`" />
      </div>
      <div class="widgetTitle">
        SPORTS
      </div>
      <SportsGameResult
        v-for="(game, i) in gamesCompleted"
        :key="game.id"
        :game="game"
        :customColor="customColor"
        :class="{ active: i === activeSlide }"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import slugify from "slugify";
import SportsGameResult from "./SportsGameResult";

const LEAGUE_NAMES = ["nba", "nfl", "nhl", "mlb"];
const API_KEY = "35530079-fb8e-4ad1-9363-516fcd";

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default {
  name: "SportsGames",
  components: { SportsGameResult },
  props: {
    customStyle: { type: Object, default: () => {} },
    customColor: { type: String, default: "" },
    customBackground: { type: String, default: "" }
  },
  data() {
    return {
      timer: null,
      timerRefresh: null,
      activeSlide: 0,
      dataIsLoaded: true,
      leagues: {
        nba: {
          teams: [],
          games: []
        },
        nfl: {
          teams: [],
          games: []
        },
        nhl: {
          teams: [],
          games: []
        },
        mlb: {
          teams: [],
          games: []
        }
      }
    };
  },
  mounted() {
    // 1. Save config:
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    LEAGUE_NAMES.map(leagueName => {
      // Read each league's team names (comma-separated)
      const teamsParam = urlParams.get(`${leagueName}Teams`) || "";
      this.leagues[leagueName].teams = teamsParam.split(",").filter(x => x);
    });
    // this.refreshDelay = ...
    // this.slideDuration = ...

    // 2. Initial data pul and slideshow setup
    this.refreshWidgetData();

    // 3. Set up data refreshing from API:
    const refreshDelay = 1000 * 60 * 60; // TODO: maybe take from query string?
    this.timerRefresh = setInterval(this.refreshWidgetData, refreshDelay);
  },
  computed: {
    gamesCompleted() {
      const games = [];
      LEAGUE_NAMES.map(leagueName => {
        this.leagues[leagueName].games
          .filter(game => game.playedStatus === "COMPLETED")
          .map(game => {
            games.push(game);
          });
      });
      return games;
    }
  },
  beforeDestroy: function() {
    clearInterval(this.timer);
    clearInterval(this.timerRefresh);
  },
  methods: {
    /**
     * Helper function that retrieves and formats team obj based on `teamId`.
     */
    getTeamFullInfo(teamReferences, teamId) {
      const teamInfo = teamReferences.find(t => t.id === teamId);
      if (!teamInfo) {
        return {};
      }
      return {
        ...teamInfo,
        icon: slugify(`${teamInfo.city} ${teamInfo.name}.png`, {
          replacement: "_",
          lower: true
        })
      };
    },
    /**
     * Helper function that receives raw games list from API data and returs list of properly formatted games.
     * Reformats and simplifies each game object, and leaves only important info.
     */
    getGamesFormatted(teamReferences, leagueName, rawGames) {
      const games = rawGames.map(game => {
        const gameObj = {
          league: leagueName,
          id: game.id,
          startTime: game.schedule.startTime,
          endedTime: game.schedule.endedTime,
          homeTeam: {
            ...this.getTeamFullInfo(teamReferences, game.schedule.homeTeam.id),
            score: game.score.homeScoreTotal
          },
          awayTeam: {
            ...this.getTeamFullInfo(teamReferences, game.schedule.awayTeam.id),
            score: game.score.awayScoreTotal
          },
          playedStatus: game.schedule.playedStatus
        };
        return gameObj;
      });
      return games;
    },
    /**
     * Fetch data from API, regarding settings from query string (e.g. league, season)
     * The idea is to return all recent games in this league (last 2 weeks)
     * plus 5 older games for local teams (teams specified in URL params).
     */
    async fetchGamesData(leagueName) {
      const season = "2019-2020-regular"; // FIXME: implement season calculating
      const url = `https://api.mysportsfeeds.com/v2.1/pull/${leagueName}/${season}/games.json`;

      // ==== 1. Fetch All games for this league that happened within last 2 weeks
      const requestConfig = {
        auth: { username: API_KEY, password: "MYSPORTSFEEDS" },
        params: { date: "from-1-weeks-ago-to-today" }
      };

      let apiResponse;
      try {
        apiResponse = await axios.get(url, requestConfig);
        apiResponse = apiResponse.data;
      } catch {
        console.error(
          "Couldn't fetch general data from sports API!",
          leagueName
        );
        return;
      }

      // Save `games` to league data.
      // Also convert games to convenient format.
      let teamReferences = (apiResponse.references || {}).teamReferences;
      const gamesAllTeams = this.getGamesFormatted(
        teamReferences,
        leagueName,
        apiResponse.games
      );

      await sleep(200);

      if (this.leagues[leagueName].teams.length === 0) {
        return gamesAllTeams;
      }

      // ==== 2. Then fetch games filtered by local teams (provided in URL params) 1 month back
      // And filter up to 5 for each team
      requestConfig.params = {
        date: "from-5-weeks-ago-to-1-weeks-ago",
        teams: this.leagues[leagueName].teams
          .map(teamName => slugify(teamName, { replacement: "-", lower: true }))
          .join(",")
      };
      try {
        apiResponse = await axios.get(url, requestConfig);
        apiResponse = apiResponse.data;
      } catch {
        console.error(
          "Couldn't fetch teams-filtered data from sports API!",
          leagueName
        );
        return;
      }

      teamReferences = (apiResponse.references || {}).teamReferences;
      const MAX_GAMES_PER_TEAM = 5;
      const gameCounts = {};
      const gamesSelectedTeams = this.getGamesFormatted(
        teamReferences,
        leagueName,
        apiResponse.games
      )
        .reverse()
        .filter(game => {
          const homeTeamName = `${game.homeTeam.city} ${game.homeTeam.name}`;
          const awayTeamName = `${game.awayTeam.city} ${game.awayTeam.name}`;
          const homeTeamCount = gameCounts[homeTeamName] || 0;
          const awayTeamCount = gameCounts[awayTeamName] || 0;
          if (
            homeTeamCount < MAX_GAMES_PER_TEAM ||
            awayTeamCount < MAX_GAMES_PER_TEAM
          ) {
            gameCounts[homeTeamName] = homeTeamCount + 1;
            gameCounts[awayTeamName] = awayTeamCount + 1;
            return true;
          }
          return false;
        })
        .reverse();

      await sleep(200);

      return [...gamesAllTeams, ...gamesSelectedTeams];
    },
    /**
     * Get data grom API and set up slideshow accordingly.
     */
    refreshWidgetData() {
      // 1. Get the data from API:
      LEAGUE_NAMES.map(async leagueName => {
        this.leagues[leagueName].games = await this.fetchGamesData(leagueName);
      });

      // 2. (Re)Set up the slideshow:
      const timerDelay = 3; // maybe take from URL?
      this.activeSlide = 0;
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.timer = setInterval(() => {
        const maxNumber = this.gamesCompleted.length;
        this.activeSlide = Math.floor(Math.random() * Math.floor(maxNumber));
      }, timerDelay * 1000);
    }
  }
};
</script>

<style>
.sedna-sports {
  font-family: "AvenirNext LT Pro", Helvetica, sans-serif;
  padding: 45px 65px;
}
.games {
  display: block;
}
.slider {
  position: relative;
  width: 100%;
  height: 100%;
}

.game {
  position: absolute;
  display: flex;
  justify-content: space-between;
  bottom: 0px;
  left: 0px;
  width: 1725px;
  height: 392px;
  box-sizing: border-box;
  opacity: 0;
  transition: opacity 0.6s ease-in-out;
}

@media screen and (max-width: 3839px) {
  .sedna-sports .widgetLogo {
    margin-top: 25px;
    margin-bottom: 0px;
    margin-left: 25px;
  }
  .game {
    width: 1725px;
    height: 392px;
  }
}

@media screen and (min-width: 3840px) {
  .sedna-sports .widgetLogo {
    margin-top: 50px;
    margin-bottom: 0px;
    margin-left: 50px;
  }
  .game {
    width: 3481px;
    height: 791px;
  }
}

.game.active {
  opacity: 1;
  transition: opacity 0.6s ease-in-out;
}
</style>
